import {
  queryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query"

import { api } from "@/lib/api-client"

import { BuildingResponse } from "../types/api"

type BuildingQueryParams = {
  search?: string
  pageIndex?: number
  pageSize?: number
  company_id?: string
}

export const getBuildingsInfiniteScroll = ({
  search,
  pageIndex,
  pageSize,
  company_id,
}: BuildingQueryParams): Promise<BuildingResponse> => {
  return api.get("/buildings", {
    params: {
      search,
      page: pageIndex,
      limit: pageSize,
      company_id,
    },
    headers: {
      "Active-Group-ID": "1",
    },
  })
}

export const getBuildingsInfiniteScrollQueryOptions = ({
  search,
  pageIndex,
  pageSize,
  company_id,
}: BuildingQueryParams) => {
  // Define the specific QueryKey type
  type BuildingsQueryKey = [
    "buildings-infinite-scroll",
    {
      search?: string
      pageIndex?: number
      pageSize?: number
      company_id?: string
    },
  ]

  return queryOptions({
    queryKey: [
      "buildings-infinite-scroll",
      { search, pageIndex, pageSize, company_id },
    ] as BuildingsQueryKey, // Assert queryKey type
    queryFn: ({ pageParam }: any) =>
      getBuildingsInfiniteScroll({
        search,
        pageIndex: pageParam,
        pageSize,
        company_id,
      }),
  })
}

type UseBuildingsInfiniteScrollOptions = {
  queryConfig?: Omit<
    UseInfiniteQueryOptions<
      BuildingResponse,
      Error,
      InfiniteData<BuildingResponse>,
      QueryKey // This QueryKey needs to match the one in queryOptions
    >,
    "queryKey" | "queryFn"
  >
} & BuildingQueryParams

export const useBuildingsInfiniteScroll = ({
  search,
  pageIndex,
  pageSize,
  company_id,
  queryConfig,
}: UseBuildingsInfiniteScrollOptions = {}) => {
  return useInfiniteQuery<
    BuildingResponse,
    Error,
    InfiniteData<BuildingResponse>,
    QueryKey, // Ensure this matches what's used in queryOptions
    number | undefined
  >({
    ...getBuildingsInfiniteScrollQueryOptions({
      search,
      pageIndex,
      pageSize,
      company_id,
    }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta.page
      const totalPage = lastPage.meta.total_page
      const nextPage = currentPage + 1

      return nextPage <= totalPage ? nextPage : undefined
    },
    initialPageParam: pageIndex,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...(queryConfig as any),
  })
}
